import "../styles/globals.css";
import { useEffect, useState, createContext, useContext } from "react";
import Head from "next/head";
import { AuthProvider, useAuth } from "../contexts/AuthContext";
import { BalanceProvider } from "../contexts/BalanceContext";
import { NotificationsProvider } from "../contexts/NotificationsContext";
import { MotionConfig } from "framer-motion";
import { Toaster } from "react-hot-toast";
import { initSocketClient, disconnectSocket } from "../../socket-client";
import { DefaultSeo } from "next-seo";
import SEO from "../../next-seo.config";

const SocketContext = createContext(null);
export const useSocket = () => useContext(SocketContext);

function MyApp({ Component, pageProps }) {
  const [socket, setSocket] = useState(null);

  const SocketInitializer = ({ children }) => {
    const { user, loading } = useAuth();
    const [isSocketInitialized, setIsSocketInitialized] = useState(false);

    useEffect(() => {
      if (typeof window === "undefined" || loading || isSocketInitialized) return;

      const initializeSocket = () => {
        const socketInstance = initSocketClient();
        setSocket(socketInstance);
        setIsSocketInitialized(true);
      };

      initializeSocket();

      return () => {
        if (user === null && !loading && socket && socket.connected) {
          console.log("Explicit logout detected, disconnecting socket");
          disconnectSocket();
          setSocket(null);
          setIsSocketInitialized(false);
        }
      };
    }, [loading, user]);

    return children;
  };

  return (
    <AuthProvider>
      <BalanceProvider>
        <NotificationsProvider>
          <SocketContext.Provider value={socket}>
            <MotionConfig>
              <SocketInitializer>
                <Head>
                  {/* Favicon Tags with Multiple Sizes */}
                  <link rel="icon" href="/favicon.ico" type="image/x-icon" />
                  <link rel="shortcut icon" href="/favicon.ico" type="image/x-icon" />
                  <link rel="apple-touch-icon" href="/apple-touch-icon.png" sizes="180x180" />

                  {/* Canonical URL */}
                  <link rel="canonical" href="https://www.essaylite.com/" />

                  {/* SEO Metadata */}
                  <meta name="description" content="Get expert writing help to craft better essays and research papers" />
                  <meta name="keywords" content="essay writing, expert help, academic assistance, plagiarism-free, fast delivery" />
                  <meta name="robots" content="index, follow" />
                  <meta name="viewport" content="width=device-width, initial-scale=1.0" />

                  {/* Open Graph Metadata */}
                  <meta property="og:url" content="https://www.essaylite.com" />
                  <meta property="og:type" content="website" />
                  <meta property="og:title" content="EssayLite – Expert Essay Help" />
                  <meta property="og:description" content="Get expert writing help to craft better essays and research papers" />
                  <meta property="og:image" content="https://i.postimg.cc/LXf8cRXW/2.png" />
                  <meta property="og:image:width" content="1200" />
                  <meta property="og:image:height" content="630" />
                  <meta property="og:site_name" content="EssayLite" />
                  <meta property="og:locale" content="en_US" />

                  {/* Twitter Card Metadata */}
                  <meta name="twitter:card" content="summary_large_image" />
                  <meta name="twitter:site" content="@www.essaylite.com" />
                  <meta name="twitter:creator" content="@essaylite" />
                  <meta name="twitter:title" content="EssayLite – Expert Essay Help" />
                  <meta name="twitter:description" content="Get expert writing help to craft better essays and research papers" />
                  <meta name="twitter:image" content="https://i.postimg.cc/LXf8cRXW/2.png" />

                  {/* Structured Data for Organization Schema */}
                  <script type="application/ld+json">
                    {JSON.stringify({
                      "@context": "https://schema.org",
                      "@type": "Organization",
                      name: "EssayLite",
                      url: "https://www.essaylite.com/",
                      logo: "https://www.essaylite.com/favicon.ico",
                      sameAs: [
                        "https://twitter.com/essaylite",
                        "https://www.linkedin.com/company/essaylite",
                      ],
                      contactPoint: {
                        "@type": "ContactPoint",
                        contactType: "Customer Service",
                        email: "support@essaylite.com",
                      },
                      description:
                        "A premier academic support platform dedicated to empowering students with expert writing guidance and innovative tools.",
                    })}
                  </script>
                </Head>

                <DefaultSeo {...SEO} />
                <Component {...pageProps} />
                <Toaster />
              </SocketInitializer>
            </MotionConfig>
          </SocketContext.Provider>
        </NotificationsProvider>
      </BalanceProvider>
    </AuthProvider>
  );
}

export default MyApp;